import React, { useEffect, useState } from 'react'
import { Box, Menu, MenuItem, Tooltip } from '@mui/material'

import { type IconName } from '~components/icons/iconMapping'
import { useOverflowCount } from '~hooks/useOverflowCount'
import { dropdownMaxHeight, spacing } from '~tokens/internal'

import Button from '../Button'
import Chip from '../Chip'
import Icon, { type SemanticIconName } from '../Icon'

import FilterCategoryPanel from './FilterCategoryPanel'
import type { FilterCategory } from './Filters'
import './Filters.scss'
import type { FilterState } from './useFilterState'

export interface FilterChipEntry {
  /** Unique key used as the React key. */
  key: string
  label: string
  icon?: SemanticIconName | IconName
  loading?: boolean
  /** Category label displayed before value pills (e.g. "Status"). */
  categoryLabel?: string
  /** Individual selected values rendered as pills inside the chip. */
  values?: string[]
  /** Tooltip text shown on hover. */
  tooltip?: string
}

/** Max pixel width for the values region before collapsing to +N. */
const VALUES_MAX_WIDTH = 250

// ---------------------------------------------------------------------------
// FilterChipLabel — structured label with value pills + overflow
// ---------------------------------------------------------------------------

interface FilterChipLabelProps {
  categoryLabel: string
  values: string[]
}

const FilterChipLabel: React.FC<FilterChipLabelProps> = ({ categoryLabel, values }) => {
  const {
    containerRef,
    visibleCount,
    shown: _shown,
    overflow,
  } = useOverflowCount(values, '.filter-chip-value', VALUES_MAX_WIDTH)

  return (
    <span className='filter-chip-label'>
      <span className='filter-chip-label__category'>{categoryLabel}</span>
      <span className='filter-chip-label__values' ref={containerRef}>
        {values.map((v, i) => (
          <span
            key={i}
            className='filter-chip-value'
            style={visibleCount !== null && i >= visibleCount ? { display: 'none' } : undefined}
          >
            {v}
          </span>
        ))}
        {overflow > 0 && <span className='filter-chip-overflow'>+{overflow}</span>}
      </span>
    </span>
  )
}

export interface FilterChipBarProps {
  /** Chip descriptors to render. */
  chips: FilterChipEntry[]
  /** Filter categories — used to render the inline category menu when a chip is clicked. */
  categories?: FilterCategory[]
  /** Current filter state — passed to a chip's inline category menu. */
  state?: FilterState
  /** Called when the filter state is changed by a chip's inline category menu. */
  onChange?: (state: FilterState) => void
  /** Called when a chip's delete button is clicked. */
  onChipDelete?: (chip: FilterChipEntry) => void
  /** Called when the "Clear all" button is clicked. Only shown when there are 2+ chips. */
  onClearAll?: () => void
  onSave?: () => void
  /** Called when the "Save as new view" option is clicked. When provided alongside onSave, renders a dropdown instead of a plain button. */
  onSaveAsNew?: () => void
}

const FilterChipBar: React.FC<FilterChipBarProps> = ({
  chips,
  categories,
  state,
  onChange,
  onChipDelete,
  onClearAll,
  onSave,
  onSaveAsNew,
}) => {
  const [chipMenuAnchorEl, setChipMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [chipMenuCategory, setChipMenuCategory] = useState<FilterCategory>()
  // Convert rem to px
  const [chipMenuMaxHeight, setChipMenuMaxHeight] = useState<number>(
    parseFloat(dropdownMaxHeight.md) * 16,
  )
  const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState<HTMLButtonElement | null>(null)
  const saveMenuOpen = Boolean(saveMenuAnchorEl)

  // Close the chip menu once its chip is gone (e.g. the last value was
  // unchecked from inside the menu); otherwise the popover loses its anchor.
  const chipMenuChipPresent = chips.some((c) => c.key === chipMenuCategory?.id)
  useEffect(() => {
    if (chipMenuCategory && !chipMenuChipPresent) {
      setChipMenuAnchorEl(null)
      setChipMenuCategory(undefined)
    }
  }, [chipMenuCategory, chipMenuChipPresent])

  if (chips.length === 0) return null

  const showSaveMenu = Boolean(onSave && onSaveAsNew)

  const getOnChipMenuClick = (
    chip: FilterChipEntry,
  ): ((e: React.MouseEvent<HTMLElement>) => void) | undefined => {
    if (!categories || !state || !onChange) return undefined
    const category = categories.find((c) => c.id === chip.key)
    if (!category) return undefined
    return (e: React.MouseEvent<HTMLElement>): void => {
      setChipMenuAnchorEl(e.currentTarget)
      setChipMenuCategory(category)
      // Calculate max height: window height - chip bottom position - chip menu spacing from the bottom of the page
      setChipMenuMaxHeight(
        window.innerHeight -
          e.currentTarget.getBoundingClientRect().bottom -
          parseFloat(spacing.xs) * 16,
      )
    }
  }

  return (
    <Box className='filter-chip-bar'>
      {chips.map((chip) => {
        const onChipMenuClick = getOnChipMenuClick(chip)
        const isStructured =
          !chip.loading && chip.categoryLabel && chip.values && chip.values.length >= 1
        return (
          <Tooltip key={chip.key} title={chip.tooltip ?? ''} placement='top'>
            <span>
              <Chip
                className={isStructured ? 'filter-chip--structured' : undefined}
                label={
                  isStructured ? (
                    <FilterChipLabel categoryLabel={chip.categoryLabel!} values={chip.values!} />
                  ) : (
                    chip.label
                  )
                }
                loading={chip.loading}
                color='blue'
                icon={chip.icon ? <Icon name={chip.icon} /> : undefined}
                onClick={onChipMenuClick}
                sx={onChipMenuClick ? { cursor: 'pointer' } : undefined}
                onDelete={onChipDelete ? (): void => onChipDelete(chip) : undefined}
              />
            </span>
          </Tooltip>
        )
      })}
      <Menu
        open={Boolean(chipMenuAnchorEl) && Boolean(chipMenuCategory) && chipMenuChipPresent}
        anchorEl={chipMenuAnchorEl}
        onClose={(): void => {
          setChipMenuAnchorEl(null)
          setChipMenuCategory(undefined)
        }}
        disableAutoFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={0}
        slotProps={{
          paper: {
            className: `filter-paper${chipMenuCategory?.type === 'date' ? ' filter-paper--date' : ''}`,
            style: {
              maxHeight: chipMenuMaxHeight,
            },
          },
        }}
      >
        {chipMenuCategory && state && onChange && (
          <FilterCategoryPanel
            key={chipMenuCategory.id}
            category={chipMenuCategory}
            state={state}
            onChange={onChange}
          />
        )}
      </Menu>
      {chips.length > 1 && onClearAll && (
        <Button variant='text' size='small' onClick={onClearAll} className='filter-clear-all'>
          Clear all
        </Button>
      )}
      {onSave && !showSaveMenu && (
        <Button variant='text' size='small' onClick={onSave} className='filter-save'>
          Save
        </Button>
      )}
      {showSaveMenu && (
        <>
          <Button
            variant='text'
            size='small'
            onClick={(e: React.MouseEvent<HTMLButtonElement>): void =>
              setSaveMenuAnchorEl(e.currentTarget)
            }
            className='filter-save'
            endIcon={<Icon name='arrowDropDown' />}
          >
            Save
          </Button>
          <Menu
            anchorEl={saveMenuAnchorEl}
            open={saveMenuOpen}
            onClose={(): void => setSaveMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            className='filter-save-menu'
          >
            <MenuItem
              onClick={(): void => {
                setSaveMenuAnchorEl(null)
                onSave?.()
              }}
              className='filter-save-menu-item'
            >
              Update current view
            </MenuItem>
            <MenuItem
              onClick={(): void => {
                setSaveMenuAnchorEl(null)
                onSaveAsNew?.()
              }}
              className='filter-save-menu-item'
            >
              Save as new view
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  )
}

export default FilterChipBar
